var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef } from "react";
import { Form, Input, Select, Switch, InputNumber, DatePicker, Radio, Divider, TreeSelect, Row, Col, ColorPicker, } from "antd";
// 使用 Form.Provider 作为 FormProvider
var FormProvider = Form.Provider;
import dayjs from "dayjs";
var Option = Select.Option;
var TextArea = Input.TextArea;
var RadioGroup = Radio.Group;
/**
 * 通用表单组件（内核）
 * 只包含表单核心功能，不包含容器和标题栏
 */
var CompsForm = function (_a) {
    var formItems = _a.formItems, formRef = _a.formRef, _b = _a.initialValues, initialValues = _b === void 0 ? {} : _b, onValuesChange = _a.onValuesChange, formProps = _a.formProps, _c = _a.layout, layout = _c === void 0 ? "vertical" : _c, // 默认为垂直布局
    _d = _a.labelCol, // 默认为垂直布局
    labelCol = _d === void 0 ? { span: 6 } : _d, // 默认标签宽度
    _e = _a.wrapperCol, // 默认标签宽度
    wrapperCol = _e === void 0 ? { span: 18 } : _e, // 默认内容区域宽度
    validateMessages = _a.validateMessages, className = _a.className, style = _a.style, _f = _a.contentPadding, contentPadding = _f === void 0 ? 16 : _f, _g = _a.gutter, gutter = _g === void 0 ? [16, 8] : _g, // 默认水平间距16，垂直间距8
    _h = _a.noWrapper, // 默认水平间距16，垂直间距8
    noWrapper = _h === void 0 ? false : _h, _j = _a.noRowCol, noRowCol = _j === void 0 ? false : _j;
    var form = Form.useForm()[0];
    var lastInitialValuesRef = useRef("");
    var resetCountRef = useRef(0);
    // 将表单实例传递给父组件
    useEffect(function () {
        if (formRef && form) {
            formRef(form);
        }
    }, [form, formRef]);
    // 当 initialValues 变化时重置表单
    useEffect(function () {
        var _a, _b;
        if (form) {
            var currentInitialValues = JSON.stringify(initialValues);
            // 检查是否真的需要重置
            if (lastInitialValuesRef.current === currentInitialValues) {
                return; // 初始值没有变化，跳过重置
            }
            // 如果用户已经开始编辑，则不因父组件状态变化而重置，避免输入被打断
            // 仅在首次或未触碰表单时根据 initialValues 同步
            var hasTouched = form.isFieldsTouched(true);
            if (hasTouched && lastInitialValuesRef.current) {
                return;
            }
            // 渲染计数防护
            resetCountRef.current += 1;
            if (resetCountRef.current > 5) {
                console.warn("⚠️ CompsForm 重置次数过多，可能存在循环重置");
                return;
            }
            lastInitialValuesRef.current = currentInitialValues;
            // 先重置表单所有字段
            form.resetFields();
            // 如果有初始值，设置表单值
            if (Object.keys(initialValues).length > 0) {
                // 应用 transform.in 转换初始值
                var transformedValues_1 = {};
                Object.keys(initialValues).forEach(function (key) {
                    var _a, _b;
                    var item = formItems.find(function (item) { return item.name === key; });
                    var value = initialValues[key];
                    // 对于日期字段，即使没有 transform，也要尝试转换
                    if ((item === null || item === void 0 ? void 0 : item.type) === "date" && value !== undefined && value !== null) {
                        // 如果已经是 dayjs 对象，直接使用
                        if (dayjs.isDayjs(value)) {
                            transformedValues_1[key] = value;
                        }
                        else if ((_a = item === null || item === void 0 ? void 0 : item.transform) === null || _a === void 0 ? void 0 : _a.in) {
                            try {
                                var transformed = item.transform.in(value);
                                // 确保转换后的值是有效的 dayjs 对象
                                if (transformed &&
                                    (dayjs.isDayjs(transformed) ||
                                        typeof (transformed === null || transformed === void 0 ? void 0 : transformed.isValid) === "function")) {
                                    transformedValues_1[key] = transformed;
                                }
                                else {
                                    // 如果 transform 失败，尝试直接使用 dayjs 转换
                                    try {
                                        var dayjsValue = dayjs(value);
                                        if (dayjsValue.isValid()) {
                                            transformedValues_1[key] = dayjsValue;
                                        }
                                        else {
                                            console.warn("\u65E5\u671F\u521D\u59CB\u503C\u8F6C\u6362\u5931\u8D25 [".concat(key, "]: ").concat(value, ", transformed:"), transformed);
                                            transformedValues_1[key] = undefined;
                                        }
                                    }
                                    catch (e) {
                                        console.warn("\u65E5\u671F\u521D\u59CB\u503C\u8F6C\u6362\u5931\u8D25 [".concat(key, "]: ").concat(value, ", transformed:"), transformed);
                                        transformedValues_1[key] = undefined;
                                    }
                                }
                            }
                            catch (error) {
                                // 如果 transform 异常，尝试直接使用 dayjs 转换
                                try {
                                    var dayjsValue = dayjs(value);
                                    if (dayjsValue.isValid()) {
                                        transformedValues_1[key] = dayjsValue;
                                    }
                                    else {
                                        console.warn("\u65E5\u671F\u521D\u59CB\u503C\u8F6C\u6362\u5F02\u5E38 [".concat(key, "]: ").concat(value), error);
                                        transformedValues_1[key] = undefined;
                                    }
                                }
                                catch (e) {
                                    console.warn("\u65E5\u671F\u521D\u59CB\u503C\u8F6C\u6362\u5F02\u5E38 [".concat(key, "]: ").concat(value), error);
                                    transformedValues_1[key] = undefined;
                                }
                            }
                        }
                        else {
                            // 如果没有 transform，尝试直接转换
                            try {
                                var date = dayjs(value);
                                if (date.isValid()) {
                                    transformedValues_1[key] = date;
                                }
                                else {
                                    transformedValues_1[key] = undefined;
                                }
                            }
                            catch (error) {
                                console.warn("\u65E5\u671F\u521D\u59CB\u503C\u76F4\u63A5\u8F6C\u6362\u5931\u8D25 [".concat(key, "]: ").concat(value), error);
                                transformedValues_1[key] = undefined;
                            }
                        }
                    }
                    else if (((_b = item === null || item === void 0 ? void 0 : item.transform) === null || _b === void 0 ? void 0 : _b.in) &&
                        value !== undefined &&
                        value !== null) {
                        try {
                            transformedValues_1[key] = item.transform.in(value);
                        }
                        catch (error) {
                            console.warn("\u521D\u59CB\u503C\u8F6C\u6362\u5931\u8D25 [".concat(key, "]: ").concat(value), error);
                            transformedValues_1[key] = value;
                        }
                    }
                    else {
                        transformedValues_1[key] = value;
                    }
                });
                console.log("🔍 CompsForm 设置表单初始值:", JSON.stringify({
                    initialValues: initialValues,
                    transformedValues: transformedValues_1,
                    timestamp: new Date().toISOString(),
                    callStack: (_a = new Error().stack) === null || _a === void 0 ? void 0 : _a.split("\n").slice(1, 4).map(function (line) { return line.trim(); }),
                }, null, 2));
                form.setFieldsValue(transformedValues_1);
                // 确保所有日期字段的值都是 dayjs 对象
                // 通过 getFieldsValue 和 setFieldsValue 触发 normalize
                setTimeout(function () {
                    var currentValues = form.getFieldsValue();
                    var dateFields = formItems.filter(function (item) { return item.type === "date"; });
                    var needsUpdate = false;
                    var updatedValues = {};
                    dateFields.forEach(function (item) {
                        if (item.name && currentValues[item.name] !== undefined) {
                            var value = currentValues[item.name];
                            // 如果不是 dayjs 对象，尝试转换
                            if (!dayjs.isDayjs(value) &&
                                value !== null &&
                                value !== undefined) {
                                try {
                                    var dayjsValue = dayjs(value);
                                    if (dayjsValue.isValid()) {
                                        updatedValues[item.name] = dayjsValue;
                                        needsUpdate = true;
                                    }
                                }
                                catch (e) {
                                    // 忽略错误
                                }
                            }
                        }
                    });
                    if (needsUpdate) {
                        form.setFieldsValue(updatedValues);
                    }
                }, 0);
            }
            else {
                // 确保表单完全清空
                var emptyValues_1 = {};
                formItems.forEach(function (item) {
                    if (typeof item.name === "string") {
                        emptyValues_1[item.name] = undefined;
                    }
                });
                console.log("🔍 CompsForm 清空表单值:", JSON.stringify({
                    emptyValues: emptyValues_1,
                    timestamp: new Date().toISOString(),
                    callStack: (_b = new Error().stack) === null || _b === void 0 ? void 0 : _b.split("\n").slice(1, 4).map(function (line) { return line.trim(); }),
                }, null, 2));
                form.setFieldsValue(emptyValues_1);
            }
            if (process.env.NODE_ENV === "development") {
                var logLevel = localStorage.getItem("arraySelectorLogLevel");
                if (logLevel && parseInt(logLevel, 10) >= 4) {
                    console.log("CompsForm 表单已重置, 初始值:", initialValues);
                }
            }
        }
    }, [initialValues, form, formItems]);
    // 定期重置计数，避免长期累积
    useEffect(function () {
        var resetTimer = setInterval(function () {
            resetCountRef.current = 0;
        }, 3000); // 每3秒重置一次
        return function () {
            clearInterval(resetTimer);
        };
    }, []);
    // 渲染表单控件
    var renderFormItem = function (item) {
        var _a, _b, _c, _d, _e;
        var type = item.type, options = item.options, _f = item.props, props = _f === void 0 ? {} : _f;
        switch (type) {
            case "input":
                return (_jsx(Input, __assign({ size: (_a = props.size) !== null && _a !== void 0 ? _a : "middle", style: __assign({ width: "100%" }, (props.style || {})) }, props)));
            case "textarea":
                return _jsx(TextArea, __assign({ rows: 4 }, props));
            case "select":
                return (_jsx(Select, __assign({ size: (_b = props.size) !== null && _b !== void 0 ? _b : "middle", style: __assign({ width: "100%" }, (props.style || {})) }, props, { children: options === null || options === void 0 ? void 0 : options.map(function (opt, index) {
                        var _a, _b;
                        return (_jsx(Option
                        // 注意：0 是合法值，不能用 `||` 否则会被当成空值
                        , __assign({ value: ((_a = opt.value) !== null && _a !== void 0 ? _a : ""), disabled: opt.disabled }, { children: opt.label }), ((_b = opt.value) !== null && _b !== void 0 ? _b : "option-".concat(index))));
                    }) })));
            case "treeSelect":
                return (_jsx(TreeSelect, __assign({ showSearch: true, style: { width: "100%" }, styles: { popup: { root: { maxHeight: 400, overflow: "auto" } } }, placeholder: "\u8BF7\u9009\u62E9", allowClear: true, treeDefaultExpandAll: true, treeNodeFilterProp: "title", size: (_c = props.size) !== null && _c !== void 0 ? _c : "middle" }, props)));
            case "switch":
                return _jsx(Switch, __assign({}, props));
            case "number":
                return (_jsx(InputNumber, __assign({ size: (_d = props.size) !== null && _d !== void 0 ? _d : "middle", style: __assign({ width: "100%" }, (props.style || {})) }, props)));
            case "date":
                return (_jsx(DatePicker, __assign({ size: (_e = props.size) !== null && _e !== void 0 ? _e : "middle", style: __assign({ width: "100%" }, (props.style || {})) }, props)));
            case "radio":
                return (_jsx(RadioGroup, __assign({}, props, { children: options === null || options === void 0 ? void 0 : options.map(function (opt) { return (_jsx(Radio, __assign({ value: opt.value }, { children: opt.label }), opt.value)); }) })));
            case "colorPicker":
                return (_jsx(ColorPicker, __assign({}, props, { showText: true, format: "hex" })));
            case "custom":
                var CustomComponent = item.customComponent;
                return CustomComponent ? (_jsx(CustomComponent, __assign({}, props))) : (_jsx(Input, __assign({}, props)));
            default:
                return _jsx(Input, __assign({}, props));
        }
    };
    // 根据布局模式准备表单属性
    var layoutFormProps = __assign({ layout: layout }, (layout === "horizontal" ? { labelCol: labelCol, wrapperCol: wrapperCol } : {}));
    // 构建内容样式
    var contentStyle = __assign({ padding: typeof contentPadding === "number"
            ? "".concat(contentPadding, "px")
            : contentPadding }, style);
    var wrapItem = function (key, colSpan, content) {
        return noRowCol ? (_jsx(React.Fragment, { children: content }, key)) : (_jsx(Col, __assign({ span: colSpan }, { children: content }), key));
    };
    var formItemsContent = formItems.map(function (item) {
        var _a;
        // 处理分隔线类型
        if (item.type === "divider") {
            return wrapItem(item.name, 24, _jsx(Divider, __assign({ orientation: "left", style: { marginTop: 24, marginBottom: 16 } }, { children: _jsx("span", __assign({ style: { fontWeight: "bold" } }, { children: item.label })) })));
        }
        // 处理普通表单项 - 使用 colSpan，默认为 24（整行）
        var colSpan = item.colSpan || 24;
        var itemFormLayoutProps = item.labelCol != null || item.wrapperCol != null
            ? __assign(__assign({}, (item.labelCol != null ? { labelCol: item.labelCol } : {})), (item.wrapperCol != null ? { wrapperCol: item.wrapperCol } : {})) : {};
        // 如果字段有dependencies，需要使用Form.Item的shouldUpdate来动态更新
        if (item.dependencies && item.dependencies.length > 0) {
            return wrapItem(item.name, colSpan, _jsx(Form.Item, __assign({ noStyle: true, shouldUpdate: function (prevValues, currentValues) {
                    var _a;
                    // 检查依赖字段是否有变化
                    return (((_a = item.dependencies) === null || _a === void 0 ? void 0 : _a.some(function (dep) {
                        return prevValues[dep] !== currentValues[dep];
                    })) || false);
                } }, { children: function (_a) {
                    var _b, _c, _d;
                    var getFieldValue = _a.getFieldValue;
                    // 支持 hidden 为函数：在 dependencies 更新时重新判断是否隐藏
                    if (typeof item.hidden === "function") {
                        try {
                            var allValues = __assign({}, (initialValues || {}));
                            var deps = item.dependencies || [];
                            for (var _i = 0, deps_1 = deps; _i < deps_1.length; _i++) {
                                var dep = deps_1[_i];
                                allValues[dep] = getFieldValue(dep);
                            }
                            var shouldHide = item.hidden(allValues);
                            if (shouldHide)
                                return null;
                        }
                        catch (e) {
                            // ignore hidden evaluation errors, fall back to render
                        }
                    }
                    else if (item.hidden === true) {
                        return null;
                    }
                    // 根据依赖字段的值动态设置disabled
                    var disabled = (_b = item.props) === null || _b === void 0 ? void 0 : _b.disabled;
                    if ((_c = item.dependencies) === null || _c === void 0 ? void 0 : _c.includes("is_active")) {
                        var isActive = getFieldValue("is_active");
                        disabled = isActive === false;
                    }
                    // 收集依赖字段的值，传递给custom组件
                    var dependencyValues = {};
                    if (item.dependencies && item.dependencies.length > 0) {
                        item.dependencies.forEach(function (dep) {
                            dependencyValues[dep] = getFieldValue(dep);
                        });
                    }
                    var itemWithDisabled = __assign(__assign({}, item), { props: __assign(__assign(__assign({}, item.props), { disabled: disabled }), (item.type === "custom" ? dependencyValues : {})) });
                    return (_jsx(Form.Item, __assign({ name: item.name, label: item.label, rules: item.rules }, itemFormLayoutProps, { valuePropName: item.type === "switch" ? "checked" : "value", hidden: item.hidden === true, dependencies: item.dependencies, getValueProps: item.type === "date"
                            ? function (value) {
                                // 确保传递给 DatePicker 的值始终是 dayjs 对象
                                if (!value)
                                    return { value: undefined };
                                if (dayjs.isDayjs(value))
                                    return { value: value };
                                // 如果不是 dayjs 对象，尝试转换
                                try {
                                    var dayjsValue = dayjs(value);
                                    return dayjsValue.isValid()
                                        ? { value: dayjsValue }
                                        : { value: undefined };
                                }
                                catch (error) {
                                    console.warn("\u65E5\u671F getValueProps \u8F6C\u6362\u5931\u8D25: ".concat(value), error);
                                    return { value: undefined };
                                }
                            }
                            : undefined, getValueFromEvent: item.type === "date"
                            ? function (value) {
                                // 确保 DatePicker 返回的值始终是 dayjs 对象
                                if (!value)
                                    return undefined;
                                // DatePicker 应该返回 dayjs 对象，但为了安全起见，我们检查并转换
                                if (dayjs.isDayjs(value))
                                    return value;
                                // 如果不是 dayjs 对象，尝试转换
                                try {
                                    var dayjsValue = dayjs(value);
                                    return dayjsValue.isValid()
                                        ? dayjsValue
                                        : undefined;
                                }
                                catch (error) {
                                    console.warn("\u65E5\u671F getValueFromEvent \u8F6C\u6362\u5931\u8D25: ".concat(value), error);
                                    return undefined;
                                }
                            }
                            : item.type === "colorPicker"
                                ? function (color) {
                                    // ColorPicker 返回 Color 对象，需要转换为 hex 字符串
                                    if (!color)
                                        return undefined;
                                    try {
                                        return color.toHexString();
                                    }
                                    catch (error) {
                                        console.warn("\u989C\u8272 getValueFromEvent \u8F6C\u6362\u5931\u8D25: ".concat(color), error);
                                        return undefined;
                                    }
                                }
                                : undefined, normalize: item.type === "date" || ((_d = item.transform) === null || _d === void 0 ? void 0 : _d.in)
                            ? function (value) {
                                var _a, _b;
                                // normalize 在值设置到表单时调用（包括初始值和程序化设置的值）
                                if (value === undefined || value === null)
                                    return value;
                                // 检查是否已经是 dayjs 对象（对于日期字段）
                                if (item.type === "date") {
                                    // 如果已经是 dayjs 对象，直接返回
                                    if (value && dayjs.isDayjs(value)) {
                                        return value;
                                    }
                                    // 对于日期字段，必须确保值是 dayjs 对象
                                    // 即使值有 isValid 方法，如果不是 dayjs 对象，也要转换
                                    // 否则尝试转换
                                    if ((_a = item.transform) === null || _a === void 0 ? void 0 : _a.in) {
                                        try {
                                            var transformed = item.transform.in(value);
                                            // 确保转换后的值是有效的 dayjs 对象
                                            if (transformed &&
                                                (dayjs.isDayjs(transformed) ||
                                                    typeof (transformed === null || transformed === void 0 ? void 0 : transformed.isValid) ===
                                                        "function")) {
                                                return transformed;
                                            }
                                            // 如果转换失败，尝试直接使用 dayjs 转换
                                            try {
                                                var dayjsValue = dayjs(value);
                                                if (dayjsValue.isValid()) {
                                                    return dayjsValue;
                                                }
                                            }
                                            catch (e) {
                                                // 忽略错误
                                            }
                                            console.warn("\u65E5\u671F normalize \u8F6C\u6362\u5931\u8D25: ".concat(value), transformed);
                                            return undefined;
                                        }
                                        catch (error) {
                                            // 如果 transform 失败，尝试直接使用 dayjs 转换
                                            try {
                                                var dayjsValue = dayjs(value);
                                                if (dayjsValue.isValid()) {
                                                    return dayjsValue;
                                                }
                                            }
                                            catch (e) {
                                                // 忽略错误
                                            }
                                            console.warn("\u65E5\u671F normalize \u8F6C\u6362\u5F02\u5E38: ".concat(value), error);
                                            return undefined;
                                        }
                                    }
                                    else {
                                        // 如果没有 transform，尝试直接使用 dayjs 转换
                                        try {
                                            var dayjsValue = dayjs(value);
                                            if (dayjsValue.isValid()) {
                                                return dayjsValue;
                                            }
                                        }
                                        catch (error) {
                                            console.warn("\u65E5\u671F normalize \u76F4\u63A5\u8F6C\u6362\u5931\u8D25: ".concat(value), error);
                                        }
                                        return undefined;
                                    }
                                }
                                return ((_b = item.transform) === null || _b === void 0 ? void 0 : _b.in)
                                    ? item.transform.in(value)
                                    : value;
                            }
                            : undefined }, { children: renderFormItem(itemWithDisabled) })));
                } })));
        }
        // 处理函数形式的 hidden 属性
        if (typeof item.hidden === "function") {
            // 需要依赖的字段列表（包括 hidden 函数可能使用的字段）
            var allDependencies_1 = item.dependencies || [];
            return wrapItem(item.name, colSpan, _jsx(Form.Item, __assign({ noStyle: true, dependencies: allDependencies_1 }, { children: function (_a) {
                    var getFieldsValue = _a.getFieldsValue, getFieldValue = _a.getFieldValue;
                    // 构建包含所有字段值的对象
                    // 首先从 getFieldsValue() 获取所有值
                    var formValues = getFieldsValue() || {};
                    // 然后为每个依赖字段单独获取值（这确保即使 getFieldsValue() 为空也能获取到值）
                    var allValues = __assign({}, initialValues);
                    // 合并表单中的所有值
                    Object.keys(formValues).forEach(function (key) {
                        if (formValues[key] !== undefined) {
                            allValues[key] = formValues[key];
                        }
                    });
                    // 确保所有依赖字段的值都被包含（优先使用 getFieldValue，因为它能获取到最新的值）
                    allDependencies_1.forEach(function (dep) {
                        var fieldValue = getFieldValue(dep);
                        if (fieldValue !== undefined) {
                            allValues[dep] = fieldValue;
                        }
                        else if (initialValues[dep] !== undefined) {
                            allValues[dep] = initialValues[dep];
                        }
                    });
                    // 调试日志
                    if (process.env.NODE_ENV === "development") {
                        console.log("[CompsForm] Field ".concat(Array.isArray(item.name) ? item.name.join('.') : item.name, ":"), {
                            allValues: allValues,
                            dependencies: allDependencies_1,
                            formValues: formValues,
                            initialValues: initialValues,
                            shouldHide: typeof item.hidden === 'function' ? item.hidden(allValues) : (item.hidden || false),
                        });
                    }
                    var shouldHide = typeof item.hidden === 'function' ? item.hidden(allValues) : (item.hidden || false);
                    if (shouldHide)
                        return null;
                    return (_jsx(Form.Item, __assign({ name: item.name, label: item.label, rules: item.rules }, itemFormLayoutProps, { valuePropName: item.type === "switch" ? "checked" : "value" }, { children: renderFormItem(item) })));
                } })));
        }
        // 处理普通 hidden 属性
        var isHidden = item.hidden === true;
        return wrapItem(item.name, colSpan, _jsx(Form.Item, __assign({ name: item.name, label: item.label, rules: item.rules }, itemFormLayoutProps, { valuePropName: item.type === "switch" ? "checked" : "value", hidden: isHidden, dependencies: item.dependencies, getValueProps: item.type === "date"
                ? function (value) {
                    // 确保传递给 DatePicker 的值始终是 dayjs 对象
                    if (!value)
                        return { value: undefined };
                    if (dayjs.isDayjs(value))
                        return { value: value };
                    // 如果不是 dayjs 对象，尝试转换
                    try {
                        var dayjsValue = dayjs(value);
                        return dayjsValue.isValid()
                            ? { value: dayjsValue }
                            : { value: undefined };
                    }
                    catch (error) {
                        console.warn("\u65E5\u671F getValueProps \u8F6C\u6362\u5931\u8D25 [".concat(item.name, "]: ").concat(value), error);
                        return { value: undefined };
                    }
                }
                : undefined, getValueFromEvent: item.type === "date"
                ? function (value) {
                    // 确保 DatePicker 返回的值始终是 dayjs 对象
                    if (!value)
                        return undefined;
                    // DatePicker 应该返回 dayjs 对象，但为了安全起见，我们检查并转换
                    if (dayjs.isDayjs(value))
                        return value;
                    // 如果不是 dayjs 对象，尝试转换
                    try {
                        var dayjsValue = dayjs(value);
                        return dayjsValue.isValid()
                            ? dayjsValue
                            : undefined;
                    }
                    catch (error) {
                        console.warn("\u65E5\u671F getValueFromEvent \u8F6C\u6362\u5931\u8D25 [".concat(item.name, "]: ").concat(value), error);
                        return undefined;
                    }
                }
                : item.type === "colorPicker"
                    ? function (color) {
                        // ColorPicker 返回 Color 对象，需要转换为 hex 字符串
                        if (!color)
                            return undefined;
                        try {
                            return color.toHexString();
                        }
                        catch (error) {
                            console.warn("\u989C\u8272 getValueFromEvent \u8F6C\u6362\u5931\u8D25 [".concat(item.name, "]: ").concat(color), error);
                            return undefined;
                        }
                    }
                    : undefined, normalize: item.type === "date" || ((_a = item.transform) === null || _a === void 0 ? void 0 : _a.in)
                ? function (value) {
                    var _a, _b;
                    // normalize 在值设置到表单时调用（包括初始值和程序化设置的值）
                    // 这是确保日期值正确转换的关键位置
                    if (value === undefined || value === null) {
                        return value;
                    }
                    // 对于日期字段，确保值始终是 dayjs 对象
                    if (item.type === "date") {
                        // 如果已经是 dayjs 对象，直接返回
                        if (value && dayjs.isDayjs(value)) {
                            return value;
                        }
                        // 对于日期字段，必须确保值是 dayjs 对象
                        // 即使值有 isValid 方法，如果不是 dayjs 对象，也要转换
                        // 否则尝试转换（可能是字符串或其他格式）
                        if ((_a = item.transform) === null || _a === void 0 ? void 0 : _a.in) {
                            try {
                                var transformed = item.transform.in(value);
                                // 确保转换后的值是有效的 dayjs 对象
                                if (transformed &&
                                    (dayjs.isDayjs(transformed) ||
                                        typeof (transformed === null || transformed === void 0 ? void 0 : transformed.isValid) === "function")) {
                                    return transformed;
                                }
                                // 如果转换失败，尝试直接使用 dayjs 转换
                                try {
                                    var dayjsValue = dayjs(value);
                                    if (dayjsValue.isValid()) {
                                        return dayjsValue;
                                    }
                                }
                                catch (e) {
                                    // 忽略错误，继续处理
                                }
                                // 如果转换失败，返回 undefined 而不是原始值
                                console.warn("\u65E5\u671F normalize \u8F6C\u6362\u5931\u8D25 [".concat(item.name, "]: ").concat(value, ", transformed:"), transformed);
                                return undefined;
                            }
                            catch (error) {
                                // 如果 transform 失败，尝试直接使用 dayjs 转换
                                try {
                                    var dayjsValue = dayjs(value);
                                    if (dayjsValue.isValid()) {
                                        return dayjsValue;
                                    }
                                }
                                catch (e) {
                                    // 忽略错误
                                }
                                console.warn("\u65E5\u671F normalize \u8F6C\u6362\u5F02\u5E38 [".concat(item.name, "]: ").concat(value), error);
                                return undefined;
                            }
                        }
                        else {
                            // 如果没有 transform，尝试直接使用 dayjs 转换
                            try {
                                var dayjsValue = dayjs(value);
                                if (dayjsValue.isValid()) {
                                    return dayjsValue;
                                }
                            }
                            catch (error) {
                                console.warn("\u65E5\u671F normalize \u76F4\u63A5\u8F6C\u6362\u5931\u8D25 [".concat(item.name, "]: ").concat(value), error);
                            }
                            return undefined;
                        }
                    }
                    // 非日期字段的正常转换
                    if ((_b = item.transform) === null || _b === void 0 ? void 0 : _b.in) {
                        try {
                            return item.transform.in(value);
                        }
                        catch (error) {
                            console.warn("\u5B57\u6BB5 normalize \u8F6C\u6362\u5931\u8D25 [".concat(item.name, "]: ").concat(value), error);
                            return value;
                        }
                    }
                    return value;
                }
                : undefined }, { children: renderFormItem(item) })));
    });
    var formBody = noRowCol ? (formItemsContent) : (_jsx(Row, __assign({ gutter: gutter }, { children: formItemsContent })));
    var formElement = (_jsx(FormProvider, { children: _jsx(Form, __assign({ form: form, initialValues: initialValues, validateMessages: validateMessages, autoComplete: "off", onValuesChange: function (changedValues, allValues) {
                var _a;
                console.log("🔍 Form onValuesChange 被触发:", JSON.stringify({
                    changedValues: changedValues,
                    allValues: allValues,
                    timestamp: new Date().toISOString(),
                    callStack: (_a = new Error().stack) === null || _a === void 0 ? void 0 : _a.split("\n").slice(1, 4).map(function (line) { return line.trim(); }),
                }, null, 2));
                if (onValuesChange) {
                    onValuesChange(changedValues, allValues);
                }
            } }, layoutFormProps, formProps, (noWrapper && { style: contentStyle, className: className }), { children: formBody })) }));
    return noWrapper ? (formElement) : (_jsx("div", __assign({ className: className, style: contentStyle }, { children: formElement })));
};
export default CompsForm;
